import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  SubmitButton,
  BadRequestError,
  Heading,
  useBrowser,
  useNotifier,
  useTranslateError,
  url,
  withBackend,
  backendShape,
} from "lcm-iot-commons";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom-v5-compat";
import SignUpBreadcrumb from "./SignUpBreadcrumb";
import { useSignUp } from "../../context/SignUpContext";

interface UrlParameters {
  confirmation_token?: string | null;
  redirect_uri?: string | null;
  email_changed?: string | null;
}

interface ConfirmationResponse {
  email: string;
  first_name: string;
  last_name: string;
}

type Props = {
  readonly backend: backendShape;
};

export function AccountConfirmation({ backend }: Props) {
  const [urlParameters, setUrlParameters] = useState<UrlParameters>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const browser = useBrowser();
  const notifier = useNotifier();
  const intl = useIntl();
  const signUp = useSignUp();
  const translateError = useTranslateError();

  const getUrlParameters = useCallback((): UrlParameters => {
    if (!location?.search) {
      return {};
    }

    const searchParams = new URLSearchParams(location.search);
    return {
      confirmation_token: searchParams.get("confirmation_token"),
      redirect_uri: searchParams.get("redirect_uri"),
      email_changed: searchParams.get("email_changed"),
    };
  }, [location?.search]);

  useEffect(() => {
    const parameters = getUrlParameters();
    if (parameters.confirmation_token) {
      setUrlParameters(parameters);
    } else {
      navigate(url("/500"));
    }
  }, [getUrlParameters, navigate]);

  // Computed values
  const emailChanged = urlParameters.email_changed === "true";

  const messageKeys = {
    message1: emailChanged
      ? "account_confirmation.email_change.message_1"
      : "account_confirmation.new_user.message_1",
    message2: emailChanged
      ? "account_confirmation.email_change.message_2"
      : "account_confirmation.new_user.message_2",
    button: emailChanged
      ? "account_confirmation.email_change.button"
      : "account_confirmation.new_user.button",
    success: emailChanged
      ? "account_confirmation.email_change.success"
      : "account_confirmation.new_user.success",
  };

  const handleError = (error: unknown) => {
    if (error instanceof BadRequestError) {
      const errorMappings = {
        already_confirmed: "error.user_confirmation.already_confirmed",
        token_expired: "error.user_confirmation.token_expired",
        token_invalid: "error.user_confirmation.token_invalid",
        token_blank: "error.user_confirmation.token_blank",
      } as const;

      const errorType = Object.keys(errorMappings).find((type) =>
        error.contains(type),
      ) as keyof typeof errorMappings;

      if (errorType) {
        notifier.showError(
          intl.formatMessage({ id: errorMappings[errorType] }),
        );
      } else {
        notifier.showError(translateError(error));
      }
    } else {
      notifier.showError(translateError(error));
    }
  };

  const handleRedirect = () => {
    if (emailChanged) {
      if (urlParameters.redirect_uri?.trim()) {
        browser.redirectToExtern(urlParameters.redirect_uri);
      } else {
        browser.redirectTo("/");
      }
    } else {
      navigate(url("/sign_up_complete"));
    }
  };

  const handleOnSubmit = async () => {
    if (isSubmitting || isSubmittingRef.current) return;

    setIsSubmitting(true);
    isSubmittingRef.current = true;

    try {
      const redirectUrl = urlParameters.redirect_uri?.trim() || "/";
      const confirmationResponse: ConfirmationResponse = await backend.get(
        "/user_confirmation",
        urlParameters,
      );

      notifier.showSuccess(intl.formatMessage({ id: messageKeys.success }));
      signUp.updateSignUpDetails({
        email: confirmationResponse.email,
        firstName: confirmationResponse.first_name,
        lastName: confirmationResponse.last_name,
        redirectUrl,
      });

      if (emailChanged) {
        await backend.delete("/session").catch((error: unknown) => {
          notifier.showError(backend.translateError(error));
        });
      }

      handleRedirect();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      {!emailChanged && (
        <SignUpBreadcrumb current="verification" action="signup" />
      )}
      <Heading
        id="account-confirmation-header"
        title={intl.formatMessage({ id: "account_confirmation.header" })}
      />
      <p id="account-confirmation-message-1" className="no-space-after">
        {intl.formatMessage({ id: messageKeys.message1 })}
      </p>
      <p id="account-confirmation-message-2" className="space-after">
        {intl.formatMessage({ id: messageKeys.message2 })}
      </p>
      <SubmitButton
        id="account-confirmation-button"
        text={intl.formatMessage({ id: messageKeys.button })}
        onClick={handleOnSubmit}
        fetching={isSubmitting}
        disabled={!urlParameters.confirmation_token || isSubmitting}
      />
    </Container>
  );
}

export default withBackend(AccountConfirmation);
